/**
 * Form state rules for the Vertex AI provider.
 *
 * Vertex authenticates with the platform's own Workload Identity, so the
 * form collects no credential — only which auth mode to use, an optional
 * project override, and the endpoint location.
 */

export const VERTEX_AUTH_MODES = {
    SAME_PROJECT: 'same_project',
    CROSS_PROJECT: 'cross_project',
};

export const VERTEX_DEFAULT_LOCATION = 'global';

export const VERTEX_CROSS_PROJECT_MESSAGE =
    'A dedicated cross-project mode will be supported in a future release. ' +
    'To use Vertex AI in a different GCP project today: keep "Same GCP ' +
    'project", enter the target project id in the Vertex Project field, and ' +
    "grant SpecificAI's service account roles/aiplatform.user in that " +
    'project (plus Model Garden subscriptions for third-party models like ' +
    'Claude).';

// ``global`` routes to whichever region has capacity and carries the
// widest model availability, which is why it leads and is the default.
// The multi-regions and single regions below are the ones that serve both
// Gemini and Claude; a region that serves neither would only produce an
// empty model list.
export const VERTEX_LOCATIONS = [
    { value: 'global', label: 'Global (widest model availability)' },
    { value: 'us', label: 'United States (multi-region)' },
    { value: 'eu', label: 'European Union (multi-region)' },
    { value: 'us-central1', label: 'us-central1 (Iowa)' },
    { value: 'us-east5', label: 'us-east5 (Columbus)' },
    { value: 'us-east4', label: 'us-east4 (N. Virginia)' },
    { value: 'us-west1', label: 'us-west1 (Oregon)' },
    { value: 'europe-west1', label: 'europe-west1 (Belgium)' },
    { value: 'europe-west4', label: 'europe-west4 (Netherlands)' },
    { value: 'asia-southeast1', label: 'asia-southeast1 (Singapore)' },
];

// GCP project ids: lowercase letters, digits and hyphens, starting with a
// letter. Checked here so an obvious typo is caught before a round trip
// that would fail as an opaque permission error.
const PROJECT_ID_PATTERN = /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/;

// GCS bucket names: 3-63 characters of lowercase letters, digits, dots,
// hyphens and underscores, starting and ending with a letter or digit.
// Checked locally so a typo fails here instead of as an opaque batch-job
// submission error hours later.
const GCS_BUCKET_PATTERN = /^[a-z0-9][a-z0-9._-]{1,61}[a-z0-9]$/;

// Operators paste bucket references in several shapes; accept the common
// ones and store the bare bucket name the gateway expects.
export const normalizeVertexGcsBucket = (value) =>
    (value || '')
        .trim()
        .replace(/^gs:\/\//, '')
        .replace(/\/+$/, '');

// Shown while no bucket is configured: what the operator is giving up is
// concrete money and throughput, so the nudge names it.
export const VERTEX_BATCH_BUCKET_RECOMMENDATION =
    'Strongly recommended: without a bucket, Vertex batch predictions are ' +
    'disabled - large runs fall back to slower realtime calls and lose the ' +
    '~50% batch discount and Claude batch prompt caching. Note that Claude ' +
    'batch jobs also require a regional Vertex location (e.g. us-east5); ' +
    'the global endpoint only batches Gemini models.';

export const validateVertexForm = ({ authMode, project, location, gcsBucket }) => {
    if (authMode === VERTEX_AUTH_MODES.CROSS_PROJECT) {
        return VERTEX_CROSS_PROJECT_MESSAGE;
    }
    if (authMode !== VERTEX_AUTH_MODES.SAME_PROJECT) {
        return 'Select how SpecificAI should authenticate to Vertex AI.';
    }
    const trimmedProject = (project || '').trim();
    if (trimmedProject && !PROJECT_ID_PATTERN.test(trimmedProject)) {
        return 'Vertex project must be a GCP project id (lowercase letters, digits and hyphens).';
    }
    if (location && !VERTEX_LOCATIONS.some((option) => option.value === location)) {
        return 'Select a Vertex location from the list.';
    }
    const normalizedBucket = normalizeVertexGcsBucket(gcsBucket);
    if (normalizedBucket && !GCS_BUCKET_PATTERN.test(normalizedBucket)) {
        return (
            'Batch jobs bucket must be a Cloud Storage bucket name ' +
            '(3-63 lowercase letters, digits, dots, hyphens or underscores).'
        );
    }
    return null;
};

export const buildVertexSavePayload = ({ authMode, project, location, gcsBucket }) => ({
    vertex_auth_mode: authMode,
    // Null rather than an empty string: the backend reads "no override"
    // and resolves the project from Application Default Credentials.
    vertex_project: (project || '').trim() || null,
    vertex_location: location || VERTEX_DEFAULT_LOCATION,
    // Explicit empty string, not null: the save route preserves a stored
    // bucket on null (for older clients that predate the field), while ""
    // is the deliberate "operator emptied the field" clear.
    vertex_gcs_bucket: normalizeVertexGcsBucket(gcsBucket),
});

// Reads the stored auth mode only, never the cloud-derived default: this
// gates "Clear", which must stay disabled for something that was never
// saved.
export const hasVertexConfiguration = (saved) => Boolean(saved?.vertex_auth_mode);

export const VERTEX_PLATFORM_CLOUD_GCP = 'gcp';

export const VERTEX_UNSUPPORTED_MESSAGE =
    'Vertex AI requires a GCP-hosted SpecificAI platform. This installation does not run on GCP, so Vertex AI models are unavailable.';

export const VERTEX_GCP_DEFAULT_MESSAGE =
    'Vertex AI is enabled by default because SpecificAI runs on GCP and authenticates with its own workload identity. Nothing has been saved yet - save to pin a project or location.';

export const isVertexSupportedOnPlatform = (platformCloudProvider) =>
    (platformCloudProvider || '').trim().toLowerCase() === VERTEX_PLATFORM_CLOUD_GCP;

// The mode the gateway actually resolved: what was saved, or the
// backend's cloud-derived default. The form compares against this so a
// GCP install with nothing saved does not read as an unsaved edit.
export const effectiveVertexAuthMode = (saved) =>
    saved?.vertex_auth_mode || saved?.vertex_effective_auth_mode || '';

// 'configured' | 'unsupported' | 'default_enabled' | 'not_configured'
//
// A saved mode is checked before the platform: the gateway keeps Vertex
// registered off GCP when a mode was explicitly saved (the cloud only
// decides what a *null* mode means), so a panel that called that
// "unsupported" would claim the provider is unavailable while it is
// serving models.
export const vertexPanelStatus = (saved, platformCloudProvider) => {
    if (saved?.vertex_auth_mode) return 'configured';
    if (!isVertexSupportedOnPlatform(platformCloudProvider)) return 'unsupported';
    if (saved?.vertex_effective_auth_mode) return 'default_enabled';
    return 'not_configured';
};

// Whether the panel may be edited at all. Off GCP that needs a saved
// configuration to manage; on GCP the workload identity is always usable.
export const isVertexPanelManageable = (saved, platformCloudProvider) =>
    hasVertexConfiguration(saved) || isVertexSupportedOnPlatform(platformCloudProvider);

export const canTestVertexConnection = (saved, platformCloudProvider) =>
    Boolean(effectiveVertexAuthMode(saved)) &&
    isVertexPanelManageable(saved, platformCloudProvider);

// Both sides of the location comparison fall back to the default: an
// unsaved organization has no location, the form always shows one, and
// ``buildVertexSavePayload`` sends the default anyway — so "nothing
// saved" and "global saved" are the same configuration, not an edit.
// The bucket compares in normalized form so pasting ``gs://bucket`` over
// a saved ``bucket`` does not read as an edit.
export const isVertexFormDirty = ({ authMode, project, location, gcsBucket }, saved) =>
    (authMode || '') !== effectiveVertexAuthMode(saved) ||
    (project || '').trim() !== (saved?.vertex_project || '') ||
    (location || VERTEX_DEFAULT_LOCATION) !==
        (saved?.vertex_location || VERTEX_DEFAULT_LOCATION) ||
    normalizeVertexGcsBucket(gcsBucket) !==
        normalizeVertexGcsBucket(saved?.vertex_gcs_bucket);
